import { useState } from "react";
import { useWorkspaceNavigation } from "@/react/navigation/useWorkspaceNavigation";
import { useIoService } from "./useIo";
import { downloadBlankTemplate, readFileHeaders } from "./readHeaders";
import type { MappingSuggestion } from "../services/types";

// Self-contained upload modal (theme tokens). Flow:
//   pick file → read headers → AI column-mapping (if AI available) → confirm
//   → upload(columnOverrides) → navigate to the Review & Fix page.
// When AI is unavailable the mapping step is skipped and the file is uploaded
// against the template's own headers.

export interface ImportUploadDialogProps {
    templateCode: string;
    templateLabel?: string;
    approvalRequired?: boolean;
    parentUuid?: string;
    onClose: () => void;
}

type Step = "pick" | "map" | "uploading";

export function ImportUploadDialog({
    templateCode,
    templateLabel,
    approvalRequired,
    parentUuid,
    onClose,
}: ImportUploadDialogProps) {
    const io = useIoService();
    const { orgCode, goToStack } = useWorkspaceNavigation();
    const [step, setStep] = useState<Step>("pick");
    const [file, setFile] = useState<File | null>(null);
    const [mappings, setMappings] = useState<MappingSuggestion[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const onDownloadTemplate = async () => {
        setError(null);
        try {
            const cols = await io.templateColumns(templateCode);
            const headers = cols
                .map((c) => String(c.header_label || c.field_name || "").trim())
                .filter((h) => h.length > 0);
            if (!headers.length) {
                setError("This template has no columns to download.");
                return;
            }
            const safe = (templateLabel ?? templateCode).replace(/[^a-z0-9._-]+/gi, "_");
            downloadBlankTemplate(`${safe}-template.xlsx`, headers);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    };

    const onPick = async (f: File | null) => {
        setFile(f);
        setError(null);
        setMappings([]);
        if (!f) return;
        setBusy(true);
        try {
            const headers = await readFileHeaders(f);
            const res = headers.length ? await io.suggestMapping(templateCode, headers) : { status: "empty" as const, mappings: [] };
            if (res.status === "ok" && res.mappings.length) {
                setMappings(res.mappings);
                setStep("map");
            }
            // AI unavailable / empty → stay on "pick"; user just clicks Upload.
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    };

    const doUpload = async () => {
        if (!file) return;
        setStep("uploading");
        setBusy(true);
        setError(null);
        try {
            const overrides: Record<string, string> = {};
            for (const m of mappings) {
                if (m.field) overrides[m.field] = m.header;
            }
            const res = await io.upload({
                templateCode,
                file,
                parentUuid,
                columnOverrides: Object.keys(overrides).length ? overrides : undefined,
            });
            if (!res.success || !res.run_uuid) throw new Error(res.error || "Upload failed");
            onClose();
            // actionKey is the ir.action *path* (import-review); the backend
            // resolves the action by path, then ClientActionPage mounts the
            // descriptor via its client_component (import_export.review).
            // viewMode "form" is a grammar filler — the URL grammar is
            // `actionSlug/viewMode/recordRef`, so the run uuid only parses back
            // into `recordRef` (→ the descriptor's required uuid param) when a
            // viewMode segment precedes it. Client actions ignore viewMode.
            goToStack([{ kind: "action", actionKey: "import-review", viewMode: "form", recordRef: res.run_uuid }]);
            void orgCode; // org is resolved inside goToStack
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
            setStep(mappings.length ? "map" : "pick");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div
                className="bg-card border border-border rounded-lg shadow-xl w-full max-w-lg"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-6 py-4 border-b border-border flex items-center gap-2">
                    <h2 className="text-base font-semibold text-foreground">Import — {templateLabel ?? templateCode}</h2>
                    {approvalRequired && (
                        <span className="ml-auto text-[11px] px-2 py-0.5 rounded bg-muted text-muted-foreground">Approval required</span>
                    )}
                </div>

                <div className="p-6 space-y-4">
                    {step !== "map" && (
                        <label className="block cursor-pointer">
                            <div className="rounded-xl border-2 border-dashed border-border hover:border-primary transition-colors p-7 text-center">
                                <div className="text-sm font-medium text-foreground">
                                    {file ? file.name : "Choose a file, or click to browse"}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">.xlsx, .xls or .csv</div>
                            </div>
                            <input
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                className="hidden"
                                disabled={busy}
                                onChange={(e) => void onPick(e.target.files?.[0] ?? null)}
                            />
                        </label>
                    )}

                    {step !== "map" && (
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">Not sure of the format?</span>
                            <button
                                type="button"
                                onClick={() => void onDownloadTemplate()}
                                className="font-medium text-primary hover:underline"
                            >
                                ⬇ Download blank template
                            </button>
                        </div>
                    )}

                    {step === "map" && (
                        <div>
                            <div className="text-sm font-medium text-foreground mb-1">✨ AI matched your columns</div>
                            <div className="text-xs text-muted-foreground mb-3">
                                Your file's headers were lined up to the template. Adjust any that look off, then continue.
                            </div>
                            <div className="space-y-1.5 max-h-72 overflow-y-auto">
                                {mappings.map((m, i) => (
                                    <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-muted/40">
                                        <span className="font-mono text-xs px-2 py-0.5 rounded bg-background border border-border">{m.header}</span>
                                        <span className="text-muted-foreground">→</span>
                                        <input
                                            value={m.field}
                                            onChange={(e) => {
                                                const next = [...mappings];
                                                next[i] = { ...m, field: e.target.value };
                                                setMappings(next);
                                            }}
                                            placeholder="(ignore)"
                                            className="flex-1 text-sm font-mono border border-input rounded px-2 py-1 bg-background outline-none focus:border-primary"
                                        />
                                        <span className="text-[10px] text-muted-foreground">{Math.round(m.confidence * 100)}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {error && <div className="p-3 rounded-md border border-destructive/40 bg-destructive/5 text-sm text-destructive">{error}</div>}
                </div>

                <div className="px-6 py-4 border-t border-border flex justify-end gap-2 bg-muted/30">
                    <button onClick={onClose} disabled={busy} className="px-4 py-2 rounded-md border border-border text-sm text-foreground hover:bg-muted disabled:opacity-40">
                        Cancel
                    </button>
                    <button
                        onClick={doUpload}
                        disabled={!file || busy}
                        className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-40"
                    >
                        {step === "uploading" ? "Uploading…" : step === "map" ? "Looks right — import" : "Upload"}
                    </button>
                </div>
            </div>
        </div>
    );
}
